import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';
import { X, CheckCircle, Loader2, Download, IndianRupee, Send } from 'lucide-react';
import { submitLead, REQUEST_TYPES } from '@/lib/web3forms';
import { fetchProperties } from '@/lib/sheetData';

const TITLES = {
  Brochure: 'Download Brochure',
  Pricing: 'Get Pricing Details',
  'General Enquiry': 'Send Enquiry',
};

const SUCCESS_MSG = {
  Brochure: 'Thank you! Our team will send the brochure shortly.',
  Pricing: 'Thank you! Our team will share pricing details shortly.',
  'General Enquiry': 'Thank you. Our team will get back to you shortly.',
};

export const LeadForm = ({
  open,
  onClose,
  requestType = REQUEST_TYPES.GENERAL,
  interestedIn = '',
  brochureLink = '',
  pricingLink = '',
  allowChooseListing = false, // when true, fetches sheet listings to choose from
  pageSource = '',
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ name: '', phone: '', email: '', listing: interestedIn });
  const [listings, setListings] = useState([]);

  useEffect(() => {
    setForm((f) => ({ ...f, listing: interestedIn }));
  }, [interestedIn, open]);

  useEffect(() => {
    if (!open) {
      setSubmitted(false);
      setError('');
      setForm({ name: '', phone: '', email: '', listing: interestedIn });
    }
  }, [open, interestedIn]);

  useEffect(() => {
    if (allowChooseListing && open && listings.length === 0) {
      fetchProperties().then((all) => setListings(all)).catch(() => {});
    }
  }, [allowChooseListing, open, listings.length]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const finalInterest = form.listing || interestedIn;
      await submitLead({
        name: form.name,
        phone: form.phone,
        email: form.email,
        interestedIn: finalInterest,
        requestType,
        page: pageSource,
        brochureLink,
        pricingLink,
      });
      setSubmitted(true);
      // For brochure: open the link after a short delay (so user reads message)
      if (requestType === REQUEST_TYPES.BROCHURE && brochureLink) {
        setTimeout(() => {
          window.open(brochureLink, '_blank', 'noopener,noreferrer');
        }, 800);
      }
      setTimeout(() => {
        onClose && onClose();
      }, 2500);
    } catch (err) {
      setError(err.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const Icon =
    requestType === REQUEST_TYPES.BROCHURE
      ? Download
      : requestType === REQUEST_TYPES.PRICING
      ? IndianRupee
      : Send;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
          onClick={onClose}
          data-testid="lead-form-overlay"
        >
          <motion.div
            initial={{ scale: 0.92, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.92, opacity: 0, y: 20 }}
            transition={{ type: 'spring', stiffness: 320, damping: 28 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-xl p-7 md:p-8 max-w-md w-full shadow-2xl relative"
            data-testid="lead-form-modal"
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-900"
              data-testid="lead-form-close"
            >
              <X size={22} />
            </button>

            <div className="flex items-center gap-3 mb-1">
              <Icon className="text-[#0B3D2E]" size={24} />
              <h2 className="text-xl font-heading text-gray-900">{TITLES[requestType]}</h2>
            </div>
            {interestedIn ? (
              <p className="text-gray-500 text-sm mb-5">For <span className="text-[#0B3D2E] font-medium">{interestedIn}</span></p>
            ) : (
              <p className="text-gray-400 text-sm mb-5">Fill in the form below</p>
            )}

            {submitted ? (
              <div className="text-center py-6" data-testid="lead-form-success">
                <CheckCircle className="text-[#0B3D2E] mx-auto mb-3" size={42} />
                <p className="text-gray-900 font-medium">{SUCCESS_MSG[requestType]}</p>
                {requestType === REQUEST_TYPES.BROCHURE && !brochureLink && (
                  <p className="text-gray-500 text-xs mt-2">Brochure coming soon — we'll email it to you.</p>
                )}
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-3.5" data-testid="lead-form">
                <input
                  type="text"
                  placeholder="Full Name *"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-gray-900 focus:border-[#0B3D2E] outline-none"
                  data-testid="lead-name"
                />
                <div className="flex border border-gray-200 rounded-lg overflow-hidden">
                  <span className="px-3 py-3 bg-gray-50 text-gray-500 text-sm flex items-center">+91</span>
                  <input
                    type="tel"
                    placeholder="Phone Number *"
                    required
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="flex-1 px-4 py-3 text-gray-900 focus:outline-none"
                    data-testid="lead-phone"
                  />
                </div>
                <input
                  type="email"
                  placeholder="Email *"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-gray-900 focus:border-[#0B3D2E] outline-none"
                  data-testid="lead-email"
                />

                {allowChooseListing ? (
                  <select
                    required
                    value={form.listing}
                    onChange={(e) => setForm({ ...form, listing: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-gray-900 focus:border-[#0B3D2E] outline-none bg-white"
                    data-testid="lead-listing"
                  >
                    <option value="">Interested Project / Listing *</option>
                    {listings.map((l) => (
                      <option key={l.id} value={l.title}>
                        {l.title} {l.location ? `— ${l.location}` : ''}
                      </option>
                    ))}
                    <option value="General Enquiry">General Enquiry</option>
                  </select>
                ) : (
                  <input
                    type="text"
                    placeholder="Interested Project / Listing"
                    value={form.listing}
                    onChange={(e) => setForm({ ...form, listing: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-gray-900 focus:border-[#0B3D2E] outline-none"
                    data-testid="lead-listing"
                  />
                )}

                {error && (
                  <p className="text-red-500 text-sm" data-testid="lead-form-error">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#0B3D2E] text-white py-3 rounded-lg font-medium hover:bg-[#C5982A] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="lead-submit"
                >
                  {submitting ? (<><Loader2 size={16} className="animate-spin" />Submitting...</>) : 'Submit'}
                </button>
                <p className="text-[11px] text-gray-400 text-center pt-1">
                  By submitting, you agree to be contacted by The NRI Realtor.
                </p>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LeadForm;
